import { styleConstants } from '@paywithextend/components'
import { purple400 } from 'ext-ui/style/colors'
import { font_size_md, font_size_sm } from 'ext-ui/style/typography'
import React from 'react'
import {
  Bar,
  BarChart,
  CartesianGrid,
  LabelList,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import { useCurrencyFormatter } from 'src/localization/Crncy'
import styled, { css } from 'styled-components'

/**
 * Data structure for each bar in the InsightsBarChart.
 *
 * Example data format:
 * const mockData: InsightsBarChartData[] = [
 *   { label: 'Flights', spent2024: 1200, spent2025: 1500 },
 *   { label: 'Hotels', spent2024: 900, spent2025: 1100 },
 *   { label: 'Taxi', spent2024: 400, spent2025: 600 },
 *   { label: 'Restaurants', spent2024: 700, spent2025: 950 },
 *   { label: 'Office Supplies', spent2024: 300, spent2025: 450 },
 * ];
 */
export interface InsightsBarChartData {
  label: string
  [key: string]: string | number | undefined // value fields for each bar
}

export interface InsightsBarChartProps {
  data: InsightsBarChartData[]
  height?: number | string
  /**
   * Keys in data to render as bars. If not provided, defaults to ['value'] for backward compatibility.
   */
  barKeys?: string[]
  /**
   * Colors for each bar group. If not provided, uses defaultBarColor for all bars (purple400).
   */
  barColors?: string[]
  xAxisLabel?: string
  yAxisLabel?: string
  tooltipFormatter?: (
    value: number,
    name: string,
    item: any,
    index: number,
  ) => React.ReactNode
  className?: string
}

const ChartContainer = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  padding-bottom: 28px; // Ensures XAxis label is not cut off
`

const defaultBarColor = purple400

const InsightsBarChart: React.FC<InsightsBarChartProps> = ({
  data,
  height = 260,
  barKeys = ['value'],
  barColors = [defaultBarColor],
  xAxisLabel,
  yAxisLabel,
  tooltipFormatter,
  className,
}) => {
  const formatCurrency = useCurrencyFormatter()

  return (
    <ChartContainer className={className}>
      <ResponsiveContainer
        css={css`
          .recharts-surface {
            overflow: visible;
          }
        `}
        width="100%"
        height={height}
      >
        <BarChart
          data={data}
          barCategoryGap={barKeys.length > 1 ? '10%' : '20%'}
        >
          <CartesianGrid
            strokeDasharray="3 3"
            stroke={styleConstants.colors.gray_dark}
            opacity="0.5"
          />
          <XAxis
            dataKey="label"
            axisLine={{ stroke: styleConstants.colors.gray_dark }}
            tickLine={false}
            tick={{
              fontSize: font_size_sm,
              fill: styleConstants.colors.gray_darkest,
            }}
            label={
              xAxisLabel
                ? {
                    value: xAxisLabel,
                    position: 'insideBottom',
                    offset: -12,
                    fill: styleConstants.colors.gray_darkest,
                    fontSize: font_size_md,
                  }
                : undefined
            }
          />
          <YAxis
            axisLine={false}
            tickLine={false}
            tick={{
              fontSize: font_size_sm,
              fill: styleConstants.colors.gray_darkest,
            }}
            label={
              yAxisLabel
                ? {
                    value: yAxisLabel,
                    angle: -90,
                    position: 'insideLeft',
                    fill: styleConstants.colors.gray_darkest,
                    fontSize: font_size_md,
                  }
                : undefined
            }
            allowDecimals={false}
          />
          <Tooltip
            cursor={{ fill: styleConstants.colors.white, opacity: 0.5 }}
            formatter={
              tooltipFormatter ||
              ((value: number, name: string, item: any, index: number) => value)
            }
            contentStyle={{
              borderRadius: 8,
              border: 'none',
              boxShadow: '0 2px 8px #0001',
              fontSize: font_size_md,
            }}
          />
          {barKeys.map((key, idx) => (
            <Bar
              key={key}
              dataKey={key}
              fill={barColors[idx] || defaultBarColor}
              radius={0}
              isAnimationActive={false}
              // Add a little offset for grouped bars
              barSize={barKeys.length > 1 ? 24 : undefined}
            >
              <LabelList
                dataKey={key}
                position="top"
                fill="#374151"
                fontSize={12}
                formatter={(value: number) => {
                  // Hide label if value is 0
                  if (value === 0) {
                    return ''
                  }
                  // Format as currency for spend values
                  if (
                    key.toLowerCase().includes('spend') ||
                    key.toLowerCase().includes('usd') ||
                    key.toLowerCase().includes('cad')
                  ) {
                    return formatCurrency({ amount: value })
                  }
                  return value
                }}
              />
            </Bar>
          ))}
        </BarChart>
      </ResponsiveContainer>
    </ChartContainer>
  )
}

export default InsightsBarChart
